import type { FixedChildSpec } from "@tento-ui/components/sheet/fixed-child-culling";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import {
  type BindCellContext,
  type ContentProviders,
  type FixedChildRowContext,
  type RenderPlugin,
  type RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { CalendarSheetColumn } from "@tento-lona/sheets-ui/plugins/layout/timeline-column/components/calendar-sheet-column.ts";
import { TimelineColumnLegend } from "@tento-lona/sheets-ui/plugins/layout/timeline-column-legend.ts";
import { LonaIcons, TimelineRowIcon } from "@tento-ui/ui/icons";
import { NaiveDate, TimezoneInfo } from "@tento-chrono";
import { type SheetRow } from "@tento-lona";
import type {
  LonaSdk,
  CalendarPreferences,
  TimezonePreferences,
} from "@tento-lona";
import { type Option, TimepointsChanged } from "@tento-lona/sheets";
import type { CalendarChromeBusOwner } from "@tento-lona/sheets-ui/plugins/layout/timeline-column/chrome-bus.ts";
import type { CalendarGesture } from "@tento-ui/components/calendar/calendar-gesture";
import type { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { TIMELINE_COLUMN_LAYOUT_SPEC } from "@tento-lona/sheets/platform";
import {
  DayColumnRenderer,
  bindTimelineColumnWithLoadGatesForHarness,
  resolveTimedGridConfig,
} from "./timeline-column/day-column-renderer.ts";
import { CurrentTimeClock } from "./timeline-column/current-time-clock.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

type TimelineColumnInstrumentation = {
  bindWithLoadGates(
    column: CalendarSheetColumn,
    context: BindCellContext,
    config: Parameters<DayColumnRenderer["bind"]>[2],
    provider: () => Promise<void>,
    cells: () => Promise<void>,
  ): Promise<void>;
};

/** `TimelineColumnLayoutPlugin`'s friend record (`Internals`): its constructor registers each instance. */
type TimelineColumnLayoutPluginInternals = {
  readonly instrumentation: (plugin: TimelineColumnLayoutPlugin) => TimelineColumnInstrumentation;
};
const timelineColumnInternals = new Internals<TimelineColumnLayoutPlugin, TimelineColumnLayoutPluginInternals>("TimelineColumnLayoutPlugin");

/** Opt-in browser harness door; no renderer object escapes. */
export function installTimelineColumnInstrumentationDoor(): void {
  const key = Symbol.for("@tento-lona/sheets-ui/testing:timelineColumnInstrumentation");
  const proto = TimelineColumnLayoutPlugin.prototype;
  if (Object.prototype.hasOwnProperty.call(proto, key)) return;
  Object.defineProperty(proto, key, {
    value: function (this: TimelineColumnLayoutPlugin): TimelineColumnInstrumentation {
      return timelineColumnInternals.of(this).instrumentation(this);
    },
  });
}

/**
 * `RenderPlugin.Layout { id: "timeline[column]" }` — the clean, non-switching
 * timed grid leaf: hourly divisions, current-time marker, vertical-time-axis
 * event positioning, legend integration with secondary timezones.
 *
 * A thin **composition adapter**: it forwards the RenderPlugin lifecycle to a
 * {@link DayColumnRenderer} (the day-column painter + its interaction state) and
 * owns the cross-cutting bits the renderer should not — the {@link
 * CurrentTimeClock}, the legend factory + its viewport-driven tz/ref-date
 * update, and the timepoint-invalidation re-bind. This plugin is pure-timed and
 * never switches to month: a standalone `timeline[column]` (no calendar alias)
 * renders the timed surface at any sheet timescale. The calendar body's
 * `calendar-body` type instead resolves to the generic `CalendarBodyLayoutPlugin`, which
 * delegates to THIS plugin for week/day and to the `grid` plugin for month.
 */
export class TimelineColumnLayoutPlugin
  implements RenderPlugin.Layout<CalendarSheetColumn, TimelineColumnLegend>
{
  static readonly spec: PerTypeSpec = TIMELINE_COLUMN_LAYOUT_SPEC;

  readonly role = "layout";
  readonly id = "timeline[column]";
  readonly loadData = null;

  readonly icon: HTMLTemplateElement = LonaIcons.$template(TimelineRowIcon);
  readonly legendFactory: SheetLegendClass<TimelineColumnLegend>;

  /** Latest sheet handle, captured in `onBindRow`; the invalidation
   *  subscription uses it to rebind timed-event columns. */
  #boundHandle: Option<SheetDelegate> = null;
  #unsubscribeInvalidation: Option<() => void> = null;

  #prevLegendRefDate: Option<NaiveDate> = null;
  #currLegendRefDate: Option<NaiveDate> = null;

  readonly #clock: CurrentTimeClock;
  readonly #deps: TimelineColumnLayoutPlugin.Deps;
  readonly #renderer: DayColumnRenderer;
  constructor(
    renderer: DayColumnRenderer,
    clock: CurrentTimeClock,
    deps: TimelineColumnLayoutPlugin.Deps,
  ) {
    timelineColumnInternals.register(this, TimelineColumnLayoutPlugin.#internals);
    this.#renderer = renderer;
    this.#deps = deps;
    this.#clock = clock;
    this.legendFactory = TimelineColumnLegend.factory(
      {
        tzPrefs: deps.timezonePreferences,
        calPrefs: deps.calendarPreferences,
        bus: renderer.gestures.chromeBus,
      },
      this.icon,
    );

    // Repaint timed-event columns on any timepoint-level change. A recurring
    // series' synthetic occurrences are index-only (not cells) and live in
    // columns with no cell change, so editing/deleting the series leaves stale
    // occurrence spans until reload; `rebindTimepoints` re-binds exactly the
    // rendered columns the emitted range intersects so they reconcile away.
    // All-day (`ViewportsInvalidated`) events are owned by the all-day plugin.
    this.#unsubscribeInvalidation = deps.sdk.invalidation.subscribe((event) => {
      if (event instanceof TimepointsChanged) {
        this.#boundHandle?.rebind.rebindTimepoints(event.range);
      }
    });
  }

  static readonly #internals: TimelineColumnLayoutPluginInternals = {
    instrumentation: (plugin) => ({
      bindWithLoadGates: (column, context, config, provider, cells) =>
        bindTimelineColumnWithLoadGatesForHarness(plugin.#renderer, column, context, config, provider, cells),
    }),
  };

  get makeRowOptions(): Option<RowOptionsElement.Factory> {
    return this.#deps.makeRowOptions;
  }
  get contentProviders(): ContentProviders {
    return this.#renderer.contentProviders;
  }
  columnBackgroundColor(): string {
    return this.#renderer.columnDeps.theme.columnBackgroundColor();
  }
  /** Exposed for plugin-manager gesture wiring (the shared renderer instance). */
  get gestureManager(): CalendarGesture.Manager<GestureInfo> {
    return this.#renderer.gestures.manager;
  }
  get chromeBus(): CalendarChromeBusOwner {
    return this.#renderer.gestures.chromeBus;
  }

  makeCell(): CalendarSheetColumn {
    return this.#renderer.makeCell();
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "A standard calendar you can create or update events with. The content of your calendar depends on the nested rows within them. Connect your Google, Whoop or Garmin accounts and see them in your calendar!";
  }

  async bindCell(
    $column: CalendarSheetColumn,
    context: BindCellContext,
  ): Promise<void> {
    const config = resolveTimedGridConfig(context.row);
    if (!config || config.calendarType === "list") {
      $column.style.display = "none";
      return;
    }
    await this.#renderer.bind($column, context, config);
  }

  async fixedChildrenForRow(
    row: SheetRow.Joined.Handle,
    context: FixedChildRowContext,
  ): Promise<FixedChildSpec[]> {
    const config = resolveTimedGridConfig(row);
    if (!config || config.calendarType === "list") return [];
    return this.#renderer.fixedChildren(row, context, config);
  }

  onResize($cell: CalendarSheetColumn, context: BindCellContext): void {
    this.#renderer.onResize($cell, context);
  }

  onBindRow(sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {
    // Capture the sheet handle so the invalidation subscription can drive
    // `rebind.rebindTimepoints` when timed events change.
    this.#boundHandle = sheet;
  }

  /**
   * Viewport sync — tooltip wiring (threaded into the renderer once available)
   * + the legend timezone/ref-date update for the timed grid. No-ops the legend
   * update for a `list` calendar (its legend renders the default row, not the
   * timed tz axis). At month scale the calendar body delegates to the `grid`
   * plugin instead, via the `CalendarBodyLayoutPlugin`.
   */
  onViewportColumnsChanged(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
    $legend: Option<TimelineColumnLegend>,
  ): void {
    this.#renderer.ensureTooltip(handle.tooltip);
    this.#renderer.noteVisibleDays({
      start: columns.start.pd.toRange().start.daysSinceEpoch,
      end: columns.end.pd.toRange().end.daysSinceEpoch,
    });

    const config = resolveTimedGridConfig(row);
    if (config?.calendarType === "list") return;

    if ($legend instanceof TimelineColumnLegend) {
      const dttz = columns.start.discrete();
      const refDate = dttz[0].start;

      const dateChanged = !this.#currLegendRefDate?.equals(refDate);
      const isOscillation = this.#prevLegendRefDate?.equals(refDate);

      if (dateChanged && !isOscillation) {
        this.#prevLegendRefDate = this.#currLegendRefDate;
        this.#currLegendRefDate = refDate;
        const start = dttz[1].toWallClock(refDate.withTime());
        const tzabbr = TimezoneInfo.tzabbr(start.tz.info);
        $legend.bindTzAbbr(tzabbr);
        $legend.updateSecondaryTimezones(columns);
      }
    }
  }

  onRenderedColumnsWillChange(
    _row: SheetRow.Joined.Handle,
    _range: ColumnIndex.Key.Resolved.Default.Range,
    _handle: SheetDelegate,
  ): void {}

  dispose(): void {
    this.#unsubscribeInvalidation?.();
    this.#unsubscribeInvalidation = null;
    this.#clock.dispose();
    this.#renderer.dispose();
  }
}

/** Types that go with `TimelineColumnLayoutPlugin`: `Deps`. */
export namespace TimelineColumnLayoutPlugin {
  export type Deps = {
    timezonePreferences: TimezonePreferences;
    calendarPreferences: CalendarPreferences;
    sdk: LonaSdk.Client;
    makeRowOptions: Option<RowOptionsElement.Factory>;
  };
}
